import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Checkbox, Input, Label } from '../../../components/ui';

const COPY_OPTIONS = [
  { key: 'copyToQa', label: 'QA' },
  { key: 'copyToSafety', label: 'Safety Officer' },
  { key: 'copyToCustomer', label: 'Customer' },
  { key: 'copyToWarehouse', label: 'Warehouse' },
  { key: 'copyToCommercial', label: 'Commercial' },
  { key: 'copyToOther', label: 'Other' },
];

/**
 * Grouped Copy distribution checkboxes for Change Control.
 */
const ChangeControlCopyDistribution = ({
  formData,
  setFormData,
  isEditMode = false,
  selectedRecord = null,
}) => {
  const { theme } = useTheme();
  const isViewMode = selectedRecord && !isEditMode && !setFormData;
  const data = isViewMode ? selectedRecord || {} : formData || {};

  const toggle = (key, checked) => {
    if (isViewMode || typeof setFormData !== 'function') return;
    setFormData((prev) => ({
      ...prev,
      [key]: checked,
      ...(key === 'copyToOther' && !checked ? { copyOtherNote: '' } : {}),
    }));
  };

  if (isViewMode) {
    const selected = COPY_OPTIONS.filter((o) => !!data[o.key]).map((o) => o.label);
    const otherNote = data.copyOtherNote;
    if (selected.length === 0) {
      return (
        <div className={cn('text-sm', theme.text.muted)}>None</div>
      );
    }
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {selected.map((label) => (
            <span
              key={label}
              className={cn(
                'inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border',
                theme.border.subtle,
                theme.colors.backgroundAlt,
                theme.text.primary,
              )}
            >
              {label}
            </span>
          ))}
        </div>
        {data.copyToOther && otherNote ? (
          <div className={cn('text-sm', theme.text.secondary)}>
            Other: {otherNote}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div
        className={cn(
          'flex flex-wrap gap-x-5 gap-y-3 p-3 rounded-lg border',
          theme.border.subtle,
          theme.colors.backgroundAlt,
        )}
      >
        {COPY_OPTIONS.map(({ key, label }) => (
          <label
            key={key}
            className={cn(
              'inline-flex items-center gap-2 cursor-pointer text-sm',
              theme.text.primary,
            )}
          >
            <Checkbox
              checked={Boolean(data[key])}
              onChange={(e) => toggle(key, e.target.checked)}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>

      {data.copyToOther ? (
        <div className="space-y-1">
          <Label>Other details</Label>
          <Input
            value={data.copyOtherNote || ''}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                copyOtherNote: e.target.value,
              }))
            }
            placeholder="Specify other copy recipient"
          />
        </div>
      ) : null}
    </div>
  );
};

export default ChangeControlCopyDistribution;
